/**
 * Drive / product-domain flow HTTP adapter (ISSUE-223).
 *
 * Product-domain APIs live on the Krysalis backend under `/api/v1/bridge-sessions/product-domain-sessions`.
 * Historically the UI used a separate axios instance (`localBridgeApiClient`) with base URL
 * `.../bridge-sessions`, which duplicated the main API host and skipped shared auth interceptors.
 *
 * **Default:** use the main `apiClient` (JWT, `/api/v1`) with path prefix `bridge-sessions/product-domain-sessions`.
 *
 * **Opt-in:** set `REACT_APP_DRIVE_USE_DEDICATED_LOCAL_BRIDGE=true` if `REACT_APP_LOCAL_BRIDGE_API_URL`
 * points at a separate Control Bridge instance; then requests use `localBridgeApiClient` and path
 * `/product-domain-sessions` only (legacy).
 */

import type { AxiosInstance } from 'axios';
import { apiClient } from './apiClient';
import { localBridgeApiClient } from './localBridgeApiClient';

/** When true, Drive uses `localBridgeApiClient` (dedicated host). When false, uses main `apiClient`. */
export function isDedicatedLocalBridgeForDrive(): boolean {
  return process.env.REACT_APP_DRIVE_USE_DEDICATED_LOCAL_BRIDGE === 'true';
}

/**
 * Axios instance for product-domain session API calls.
 * Prefer main API client so session auth applies to Drive flows on the same deployment.
 */
export function getDriveFlowHttpClient(): AxiosInstance | null {
  if (isDedicatedLocalBridgeForDrive()) {
    return localBridgeApiClient;
  }
  return apiClient;
}

/**
 * Path prefix for product-domain routes (no trailing slash).
 * - Main API: `/bridge-sessions/product-domain-sessions` (base URL is already `/api/v1`).
 * - Dedicated LB: `/product-domain-sessions` (base URL is `.../bridge-sessions`).
 */
export function getProductDomainSessionsBasePath(): string {
  return isDedicatedLocalBridgeForDrive()
    ? '/product-domain-sessions'
    : '/bridge-sessions/product-domain-sessions';
}

/** Whether Drive reporting components should attach Local-Bridge-style custom fetchers. */
export function isDriveFlowReportingEnabled(): boolean {
  return Boolean(getDriveFlowHttpClient());
}
